import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId, waLink, WA_MESSAGES } from "@/lib/site";

const LINKS = [
    { label: "Destinations", id: "destinations", testId: "nav-link-destinations" },
    { label: "VIP Tables", id: "vip-tables", testId: "nav-link-vip-tables" },
    { label: "How It Works", id: "how-it-works", testId: "nav-link-how-it-works" },
    { label: "FAQ", id: "faq", testId: "nav-link-faq" },
];

const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 48);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <>
            <header
                data-testid="main-nav"
                className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
                    scrolled
                        ? "border-b border-white/10 bg-[#050505]/90 backdrop-blur-md"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 md:px-10">
                    <Link
                        data-testid="nav-logo"
                        to="/"
                        className="text-sm font-extrabold uppercase tracking-[0.4em] text-ivory transition-colors duration-300 hover:text-gold"
                    >
                        BookMyVIP
                    </Link>

                    <div className="hidden items-center gap-10 md:flex">
                        {LINKS.map((l) => (
                            <button
                                key={l.id}
                                data-testid={l.testId}
                                onClick={() => go(l.id)}
                                className="text-[11px] font-semibold uppercase tracking-[0.25em] text-ivory/70 transition-colors duration-300 hover:text-gold"
                            >
                                {l.label}
                            </button>
                        ))}
                        <a
                            data-testid="nav-book-vip-btn"
                            href={waLink(WA_MESSAGES.hero)}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Opens WhatsApp"
                            className="rounded-sm bg-gold px-6 py-3 text-[11px] font-bold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-gold-soft"
                        >
                            Book VIP
                        </a>
                    </div>

                    <button
                        data-testid="nav-menu-toggle"
                        onClick={() => setOpen(!open)}
                        aria-label="Toggle menu"
                        className="text-ivory md:hidden"
                    >
                        {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
                    </button>
                </nav>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-40 flex flex-col justify-center bg-[#050505] px-8 md:hidden"
                    >
                        <div className="flex flex-col gap-8">
                            {LINKS.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    data-testid={`mobile-nav-link-${l.id}`}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 + i * 0.08, duration: 0.6 }}
                                    onClick={() => go(l.id)}
                                    className="text-left font-serif text-4xl text-ivory transition-colors duration-300 hover:text-gold"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                            <motion.a
                                data-testid="mobile-nav-book-vip-btn"
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5, duration: 0.6 }}
                                href={waLink(WA_MESSAGES.hero)}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Opens WhatsApp"
                                className="mt-4 inline-flex w-max items-center rounded-sm bg-gold px-8 py-4 text-[11px] font-bold uppercase tracking-[0.25em] text-ink"
                            >
                                Book VIP
                            </motion.a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Nav;
